import {
  ActionsMenu,
  Chip,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import moment from 'moment';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { APP, ROUTES } from 'config';
import {
  BenefitsFilter,
  DateRangeFilter,
  FilterButton,
  Filters,
  OrgStructureFilter,
  SelectFilter,
} from 'components/filters';
import {
  COLOR_BY_BENEFIT_STATUS,
  benefitCategories,
  benefitPublishStatuses,
  benefitStatuses,
  benefitsFiltersSchema,
  useBenefits,
  useExportToXls,
} from 'store/benefits';
import { formatCurrency } from 'utils/format';
import TemplateDialog from 'components/dialogs/TemplateDialog/TemplateDialog';
import NumberRangeFilter from 'components/filters/NumberRangeFilter';
import { DownloadXlsAction } from 'components/actions';
import {
  useCompanyLanguages,
  useDefaultCurrency,
  useMe,
} from 'state/Administrators';
import { useDialog } from 'utils/hooks/useDialog';
import { useSearchParams } from 'utils/navigation';
import { binaries } from 'types/Common';
import { useModalContext } from 'store/modal';
import { LanguageSelectModal } from 'components/modals';
import { useBenefitsPDFDownload } from 'components/benefits/BenefitsPDF/hooks/useMultipleBenefitsPDFDownload';
import { BenefitsBulkEditModal } from '../atoms/benefits-bulk-edit-modal';
import { CreateBenefitModal } from '../atoms/create-benefit-modal';

export const BenefitsList: FC = () => {
  const { t, i18n } = useTranslation();
  const { canEditBenefits, hasEditPermission } = useMe();
  const templatesDialog = useDialog();
  const { currency } = useDefaultCurrency();
  const { supportedLanguages } = useCompanyLanguages();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();
  const { openModal } = useModalContext();

  const { searchParams: filters, navigate } = useSearchParams(
    benefitsFiltersSchema,
  );

  const { data: benefits, isLoading } = useBenefits(filters);

  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();
  const { downloadPdf, loading: isDownloading } = useBenefitsPDFDownload();

  return (
    <>
      <TableWrapper>
        <TableToolbar>
          <Stack gap={1}>
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-end"
              gap={2}
            >
              <ActionsMenu
                type="spread"
                items={[
                  {
                    label: t('buttons.new_benefit'),
                    disabled:
                      !canEditBenefits || !hasEditPermission(['benefits']),
                    color: 'primary',
                    onClick: () => {
                      openModal({
                        component: CreateBenefitModal,
                      });
                    },
                  },
                  {
                    label: t('benefits.templates'),
                    disabled:
                      !canEditBenefits || !hasEditPermission(['benefits']),
                    onClick: () => {
                      templatesDialog.openDialog();
                    },
                  },
                ]}
              />
            </Stack>

            <Divider />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
            >
              <ListTotal total={benefits?.total} />

              <Stack direction="row" alignItems="center" gap="10px">
                <Filters
                  value={filters}
                  initialValues={benefitsFiltersSchema.parse({})}
                  onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                  onChangeCallback={() => setSelectedRowIds({})}
                >
                  {({ value, initialValues, setFilter }) => (
                    <>
                      <FilterButton
                        label={t('common.plan_name')}
                        value={value.benefits}
                        initialValue={initialValues.benefits}
                        onChange={(v) => setFilter('benefits', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <BenefitsFilter
                            showArchived={filters.showArchived}
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('benefits.benefit_type')}
                        value={value.category}
                        initialValue={initialValues.category}
                        onChange={(v) => setFilter('category', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple
                            options={benefitCategories.map((category) => ({
                              key: category,
                              label: t(`menu.${category}`),
                            }))}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('common.ends')}
                        value={value.endsAt}
                        initialValue={initialValues.endsAt}
                        onChange={(v) => setFilter('endsAt', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <DateRangeFilter
                            value={value}
                            onApply={applyFilter}
                            onClear={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('exportFileName.employees')}
                        value={value.employeeCount}
                        initialValue={initialValues.employeeCount}
                        onChange={(v) => setFilter('employeeCount', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <NumberRangeFilter
                            value={value}
                            onApplyFilter={applyFilter}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('home.organisationStructure_title')}
                        value={value.orgStructure}
                        initialValue={initialValues.orgStructure}
                        onChange={(v) => setFilter('orgStructure', v)}
                        contentMaxWidth={420}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <OrgStructureFilter
                            value={value}
                            onApplyFilter={applyFilter}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('benefits.publish_status')}
                        value={value.publishStatus}
                        initialValue={initialValues.publishStatus}
                        onChange={(v) => setFilter('publishStatus', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple
                            options={benefitPublishStatuses.map((status) => ({
                              key: status,
                              label: t(`status.${status}`),
                            }))}
                            searchable={false}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('common.current_status')}
                        value={value.status}
                        initialValue={initialValues.status}
                        onChange={(v) => setFilter('status', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple
                            options={benefitStatuses.map((status) => ({
                              key: status,
                              label: t(`status.${status}`),
                            }))}
                            searchable={false}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('benefits.filters.archived.label')}
                        value={value.showArchived}
                        initialValue={initialValues.showArchived}
                        onChange={(v) => setFilter('showArchived', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            options={binaries.map((binary) => ({
                              key: binary,
                              label: t(`common.${binary}`),
                            }))}
                            searchable={false}
                            multiple={false}
                          />
                        )}
                      </FilterButton>
                    </>
                  )}
                </Filters>

                <Divider orientation="vertical" variant="middle" flexItem />

                <DownloadXlsAction
                  listLength={benefits?.total ?? 0}
                  exportToExcel={() => exportToXls({ filters })}
                  loading={isExportingToXls}
                />
              </Stack>
            </Stack>
          </Stack>
        </TableToolbar>

        <Table
          data={benefits?.items ?? []}
          isLoading={isLoading}
          columns={[
            {
              id: 'name',
              header: t('common.plan_name'),
              cell: ({ row: { original: benefit } }) => (
                <Stack spacing={0.25} alignItems="flex-start">
                  {benefit.isArchived ? (
                    <Chip
                      label={t('benefits.isArchived.label')}
                      variant="error"
                    />
                  ) : null}
                  <Link
                    label={benefit.name}
                    to={ROUTES.benefits.details.replace(':id', benefit.id)}
                  />
                </Stack>
              ),
              meta: { sort: true },
            },
            {
              id: 'type',
              header: t('benefits.benefit_type'),
              cell: ({ row: { original: benefit } }) => {
                return t(`menu.${benefit.type}`);
              },
              meta: { sort: true },
            },
            {
              id: 'trackBalance',
              header: t('benefitExpenses.remaining'),
              cell: ({ row: { original: benefit } }) => {
                return benefit.trackBalance ? t('status.on') : null;
              },
            },
            {
              id: 'allowClaim',
              header: t('benefits.claim_expense'),
              cell: ({ row: { original: benefit } }) => {
                return benefit.allowClaimReimbursement ? t('status.on') : null;
              },
            },
            {
              id: 'value',
              header: t('benefits.value'),
              cell: ({ row: { original: benefit } }) =>
                Number.isNaN(benefit.value)
                  ? null
                  : formatCurrency(i18n.language, benefit.value, currency),
              meta: { align: 'right' },
            },
            {
              id: 'deactivationDate',
              header: t('common.ends'),
              cell: ({ row: { original: benefit } }) =>
                benefit.deactivatedAt
                  ? moment(benefit.deactivatedAt).format('l')
                  : null,
            },
            {
              id: 'employeeCount',
              header: t('exportFileName.employees'),
              cell: ({ row: { original: benefit } }) => benefit.employeeCount,
              meta: { align: 'right' },
            },
            {
              id: 'publishStatus',
              header: t('benefits.publish_status'),
              cell: ({ row: { original: benefit } }) => {
                return t(`status.${benefit.publishStatus}`);
              },
            },
            {
              id: 'status',
              header: t('common.current_status'),
              meta: { align: 'center' },
              cell: ({ row: { original: benefit } }) => (
                <Typography color={COLOR_BY_BENEFIT_STATUS[benefit.status]}>
                  {benefit.status === 'expiresSoon' && benefit.deactivatedAt
                    ? t('status.valid_for', {
                        days: moment(benefit.deactivatedAt).diff(
                          moment(),
                          'days',
                        ),
                      })
                    : t(`status.${benefit.status}`)}
                </Typography>
              ),
            },
          ]}
          rows={{
            inactive: ({ original: benefit }) => benefit.status === 'inactive',
          }}
          sort={{
            value: filters.sort,
            onChange: (v) => navigate({ params: { sort: v } }),
          }}
          selection={{
            isLoading: isDownloading,
            value: selectedRowIds,
            onChange: setSelectedRowIds,
            actions: [
              {
                label: t('common.edit'),
                disabled: !hasEditPermission(['benefits']),
                onClick: async () => {
                  const response = await openModal({
                    component: BenefitsBulkEditModal,
                    props: { benefitIds: Object.keys(selectedRowIds) },
                  });

                  if (response.action === 'SAVE') {
                    setSelectedRowIds({});
                  }
                },
              },
              {
                label: t('common.downloadPDF'),
                onClick: async () => {
                  const response = await openModal({
                    component: LanguageSelectModal,
                    props: {
                      languages: supportedLanguages,
                    },
                  });

                  if (response.action === 'CONFIRM') {
                    downloadPdf(Object.keys(selectedRowIds), response.language);
                  }
                },
              },
            ],
          }}
          pagination={
            benefits && {
              count: benefits.total,
              page: benefits.page,
              pageSize: benefits.pageSize,
              pageSizeOptions: APP.pagination.defaults.pageSize,
              onChange: (page, pageSize) => {
                navigate({ params: { page, pageSize } });
              },
            }
          }
        />
      </TableWrapper>

      <TemplateDialog
        open={templatesDialog.open}
        onClose={templatesDialog.closeDialog}
        onCreate={(template) => {
          templatesDialog.closeDialog();
          openModal({
            component: CreateBenefitModal,
            props: {
              templateId: template.id,
              initialValues: {
                name: template.title,
                category: template.category,
              },
            },
          });
        }}
      />
    </>
  );
};
